import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { RootState } from '.';
import * as authService from '../services/auth/auth.service';
import { validateAdminPanelToken } from '../services/auth/auth.service';
import sentry from 'src/logger/sentry';
import { updateAllSessionsAction } from './all-sessions.store';
import { TSession, TValidateAdminPanelTokenResponse } from 'src/types/session.type';
import { APIError } from 'src/lib/error';
import Logger from 'src/logger';

const initialState = null as TSession | null;

export const sessionSlice = createSlice({
  name: 'session',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder.addCase(getSessionAction.fulfilled, (state, action) => {
      state = action.payload;
      return state;
    });

    builder.addCase(updateSessionAction.fulfilled, (state, action) => {
      state = action.payload;
      return state;
    });

    builder.addCase(updateSessionMetadataAction.fulfilled, (state, action) => {
      // Update the session metadata without replacing the entire session
      state = action.payload;
      return state;
    });

    builder.addCase(clearSessionAction.fulfilled, (state, action) => {
      state = null;
      return state;
    });
  },
});

export const getSessionAction = createAsyncThunk('getSession/get', async (_, { dispatch }) => {
  const session = authService.getSession();
  if (!session) return null;
  sentry.updateUser(session);
  dispatch(updateAllSessionsAction(session));
  return session;
});

export const updateSessionAction = createAsyncThunk('updateSession/post', async (data: TSession, { dispatch }) => {
  authService.updateSession(data);
  sentry.updateUser(data);
  dispatch(updateAllSessionsAction(data));
  return data;
});

export const updateSessionMetadataAction = createAsyncThunk('updateSessionMetadata/post', async (data: TSession) => {
  sentry.updateUser(data);
  return data;
});

export const clearSessionAction = createAsyncThunk('clearSession/post', async () => {
  return authService.clearSession();
});

export const validateAdminPanelTokenThunk = createAsyncThunk<
  TValidateAdminPanelTokenResponse,
  void,
  { rejectValue: APIError }
>('session/validateToken', async (_, { getState, dispatch, rejectWithValue }) => {
  try {
    const response = await validateAdminPanelToken();

    const { userId, email, name } = response;

    const { session } = getState() as RootState;
    if (session) {
      const updatedSession = {
        ...session,
        customer_id: userId,
        emailAccount: email,
        name,
      };

      await dispatch(updateSessionMetadataAction(updatedSession));
    }
    return response;
  } catch (error) {
    console.error('Token validation error:', error);
    Logger.reportError(`Token validation error: ${error}`);
    return rejectWithValue(error as APIError);
  }
});

export const sessionSelector = (state: RootState) => state.session;

export default sessionSlice.reducer;
